import { EventEmitter, Injectable, Injector } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { UserCredentials } from '../models/UserCredentials';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { UserModifyResponse } from '../models/UserModifyResponse';
import { User } from '../models/User';
import { ProfileSettings } from '../models/ProfileSettings';
import { filter, map, take } from 'rxjs/operators';
import { UserLogin } from '../models/UserLogin';
import { Role } from '../models/Role';
import { AvailableForm } from '../models/AvailableForm';
import { WorkflowForm } from '../models/WorkflowForm';
import { RoleItemDto } from 'app/models/RoleItem';
import { environment } from 'environments/environment';
import { UtilityService } from 'app/services/utility.service';
import { NotificationService } from 'app/services/notification.service';
import { ClaimItem } from 'app/models/ClaimItem';
import { datadogRum } from '@datadog/browser-rum';
import { LookupServices } from 'app/services/lookup.service';
import { GeneralLedgerLookupServices } from 'app/services/general-ledger/general-ledger-lookup.service';
import { SubSegmentService } from 'app/services/form/subSegmentation.service';
import { ProjectGrantFormService } from 'app/services/project-grant/project-grant-form.service';
import { ProjectGrantTaskService } from 'app/services/project-grant/project-grant-task-service';

@Injectable({
	providedIn: 'root',
})
export class UserService {
	private readonly currentUserSubject: BehaviorSubject<UserLogin>;
	public currentUser: Observable<UserLogin>;
	private readonly availableFormsSubject: BehaviorSubject<AvailableForm[]>;
	public availableForms$: Observable<AvailableForm[]>;
	public static cookieName = 'userInfo';
	public static mappingName = 'userFormMapping';
	public static clientIdCookie = 'selectedClientId';
	public static clientIdOwnerKey = 'selectedClientIdOwner';
	public static msTokenCookie = 'msTokenCookie';
	public static msalAccount = 'msalAccount';
	public static deviceIdKey = 'deviceId';
	availableFormsEvent: EventEmitter<AvailableForm[]> = new EventEmitter<AvailableForm[]>();

	constructor(
		private readonly httpClient: HttpClient,
		private readonly utilityService: UtilityService,
		private readonly notificationService: NotificationService,
		private readonly injector: Injector
	) {
		const user = localStorage.getItem(UserService.cookieName) || sessionStorage.getItem(UserService.cookieName);
		this.currentUserSubject = new BehaviorSubject<UserLogin>(this.safeParse<UserLogin>(user, null));
		this.currentUser = this.currentUserSubject.asObservable();
		this.setDatadogRUMUser();

		const storedForms = localStorage.getItem('availableForms');
		const initialForms = this.safeParse<AvailableForm[]>(storedForms, []);
		this.availableFormsSubject = new BehaviorSubject<AvailableForm[]>(initialForms);
		this.availableForms$ = this.availableFormsSubject.asObservable();
	}

	public get currentUserValue(): UserLogin {
		return this.currentUserSubject.value;
	}

	/**
	 * A stable identifier for this browser, created on first use.
	 *
	 * Lets the server replace this browser's previous session on sign-in without disturbing the
	 * same person's session on another machine. It deliberately outlives logout — it identifies
	 * the device, not the session — so it is not among the keys cleared on sign-out.
	 */
	public get deviceId(): string {
		let deviceId = localStorage.getItem(UserService.deviceIdKey);
		if (!deviceId) {
			deviceId = typeof crypto?.randomUUID === 'function' ? crypto.randomUUID() : this.fallbackDeviceId();
			localStorage.setItem(UserService.deviceIdKey, deviceId);
		}
		return deviceId;
	}

	/**
	 * Wipes both stores but keeps the device identifier.
	 *
	 * That identifier names the browser, not the session. Losing it on sign-in would mint a new
	 * one every time, so the server would never recognise this device as the one whose session it
	 * should replace, and stale sessions would pile up per login instead of per device.
	 */
	private clearAllStorage() {
		const deviceId = localStorage.getItem(UserService.deviceIdKey);
		localStorage.clear();
		sessionStorage.clear();
		if (deviceId) {
			localStorage.setItem(UserService.deviceIdKey, deviceId);
		}
	}

	private fallbackDeviceId(): string {
		// Only reached on browsers without crypto.randomUUID. This is a grouping key, never a
		// secret, so uniqueness is all that is needed of it.
		return `d-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
	}

	public get availableForms(): AvailableForm[] {
		return this.availableFormsSubject.value;
	}

	public set availableForms(items: AvailableForm[]) {
		this.availableFormsSubject.next(items);
		localStorage.setItem('availableForms', JSON.stringify(items));
		this.availableFormsEvent.emit(items);
	}

	public get formMapping(): any {
		const raw =
			sessionStorage.getItem(UserService.mappingName) || localStorage.getItem(UserService.mappingName) || null;
		return this.safeParse(raw, {});
	}

	// returns available form id
	public getFormKeyByName(name) {
		let data = this.formMapping;
		for (const key in this.formMapping) {
			if (Object.prototype.hasOwnProperty.call(data, key)) {
				const element = data[key];
				if (element == name) {
					return key;
				}
			}
		}
		return '';
	}

	public setDatadogRUMUser() {
		if (this.currentUserValue && this.currentUserValue.user)
			datadogRum.setUser({
				id: this.currentUserValue.user.id,
				name: this.currentUserValue.user.fullName,
				email: this.currentUserValue.user.email,
				clientId: this.currentUserValue.user.clientId,
			});
	}

	resetPassword(model: UserCredentials): Observable<UserModifyResponse> {
		return this.httpClient.post<UserModifyResponse>(`${environment.apiUrl}authentication/reset-password`, model).pipe();
	}

	requestPassword(model: UserCredentials): Observable<UserModifyResponse> {
		return this.httpClient
			.post<UserModifyResponse>(`${environment.apiUrl}authentication/request-reset-password`, model)
			.pipe();
	}

	login(email: string, password: string): Observable<UserLogin> {
		return this.httpClient.post<UserLogin>(`${environment.apiUrl}authentication/login`, { email, password }).pipe();
	}

	mslogin(email: string, password: string): Observable<UserLogin> {
		return this.httpClient.post<UserLogin>(`${environment.apiUrl}authentication/mslogin`, { email, password }).pipe();
	}

	unlockUserAccount(email: string): Observable<UserModifyResponse> {
		return this.httpClient
			.post<UserModifyResponse>(`${environment.apiUrl}authentication/unlock-user-account`, { email })
			.pipe();
	}
	validateMappings() {
		if (!localStorage.getItem(UserService.mappingName)) {
			this.getFormMappingAll().subscribe((data) => {
				this.setFormMappings(data);
			});
		}
	}

	completeLogIn(userLogin, remember, onReady: () => void = () => {}) {
		// Stamp when this token was received so freshness can be judged later without decoding it.
		userLogin = { ...userLogin, receivedAt: Date.now() };
		let selectedClientId = localStorage.getItem(UserService.clientIdCookie);
		// A selection stamped with a different user's id belongs to the previous person on this
		// machine (forced logout preserves the selection; the browser may be shared). Restoring
		// it would sign this user into a client they never chose. An absent stamp means the
		// selection was written before stamping existed; treat it as this user's.
		const selectionOwner = localStorage.getItem(UserService.clientIdOwnerKey);
		if (selectionOwner && selectionOwner !== userLogin.user?.id) {
			selectedClientId = null;
		}
		let msalAccount = localStorage.getItem(UserService.msalAccount);
		this.clearAllStorage();
		this.validateMappings();

		this.setClientIdIfEmpty(userLogin.user);

		if (remember) {
			localStorage.setItem(UserService.cookieName, JSON.stringify(userLogin));
		} else {
			sessionStorage.setItem(UserService.cookieName, JSON.stringify(userLogin));
		}
		if (msalAccount) localStorage.setItem(UserService.msalAccount, msalAccount);

		// Publish the home-client session before attempting the restore: the switch-client call
		// below goes through the JwtInterceptor, which reads the session from here. Publishing
		// last (the old shape) also meant the restore's merged session was overwritten in memory
		// by this untouched local object, so the UI stayed on the home client while storage said
		// otherwise.
		this.currentUserSubject.next(userLogin);
		if (userLogin.user)
			datadogRum.setUser({
				id: userLogin.user.id,
				name: userLogin.user.fullName,
				email: userLogin.user.email,
				clientId: userLogin.user.clientId,
			});

		this.restoreSelectedClient(selectedClientId, userLogin, onReady);

		this.currentUserSubject
			.asObservable()
			.toPromise()
			.then(() => {
				location.reload();
			});
	}

	/**
	 * Puts the session back on the client the user was on before a forced logout.
	 *
	 * clearAllStorage wiped the selection pointer, and the login response is always built for
	 * the user's home client, so without this every forced logout quietly reverts a switched
	 * client. The restore is a real switch — the server authorises it (including clients the
	 * user reaches through view-all rather than assignment, which is why there is no
	 * user.clients[] gate here) and mints a token for that client. Patching the stored clientId
	 * alone would leave the claims keyed to the home client and permissions silently gone.
	 *
	 * `onReady` fires once the session is settled either way, so the caller can navigate on the
	 * final client rather than the interim home-client one.
	 */
	private restoreSelectedClient(selectedClientId: string | null, userLogin: UserLogin, onReady: () => void) {
		if (!selectedClientId || selectedClientId === userLogin.user?.clientId) {
			onReady();
			return;
		}

		this.switchClient(selectedClientId).subscribe({
			next: (switched) => {
				const currentStorageIsLocal = localStorage.getItem(UserService.cookieName) !== null;
				const storage = currentStorageIsLocal ? localStorage : sessionStorage;
				const stored = this.safeParse<UserLogin>(storage.getItem(UserService.cookieName), userLogin);
				// Merge, do not replace: the switch response carries no refresh token because the
				// caller keeps the one it holds (same contract as setClientId's getNewToken path).
				const merged: UserLogin = {
					...stored,
					...switched,
					refreshToken: switched.refreshToken ?? stored.refreshToken,
					// Only the interactive sign-in paths set this; the switch response always
					// carries the default false. Taking it would turn an SSO session into a
					// password one, and the navbar gates the Entra end-session call on it, so
					// signing out would quietly stop ending the identity-provider session.
					isSSOLogin: stored.isSSOLogin ?? switched.isSSOLogin,
					user: { ...stored.user, ...switched.user, clientId: selectedClientId },
					receivedAt: Date.now(),
				};
				storage.setItem(UserService.cookieName, JSON.stringify(merged));
				// clearAllStorage removed the pointer; write it back so the next boot agrees
				// with the restored session.
				localStorage.setItem(UserService.clientIdCookie, selectedClientId);
				this.currentUserSubject.next(merged);
				this.setDatadogRUMUser();
				onReady();
			},
			error: (err) => {
				// clearAllStorage wiped the pointer, so an active refusal needs nothing more:
				// leaving it gone stops the refusal repeating on every future sign-in. A
				// transient failure (offline, deploy blip) writes it back so the next sign-in
				// can try again.
				if (err?.status !== 401 && err?.status !== 403) {
					localStorage.setItem(UserService.clientIdCookie, selectedClientId);
				}
				this.notificationService.showError(
					'Unable to restore your previously selected client. You are signed in on your home client.'
				);
				onReady();
			},
		});
	}

	/**
	 * Silently update stored credentials after a token refresh.
	 * Unlike completeLogIn, this does NOT clear storage or reload the page —
	 * it only replaces the token and refreshToken so in-flight requests continue working.
	 *
	 * The response is merged onto the stored session rather than replacing it, so fields the
	 * refresh response does not carry (notably the selected client) survive. It aborts unless
	 * the stored session is still the same one that asked for the refresh: another tab may have
	 * logged in as someone else while this request was in flight, and writing this response over
	 * that would hand the new session the old user's token.
	 *
	 * @param sentRefreshToken the refresh token this response was requested with.
	 */
	completeTokenRefresh(userLogin: UserLogin, sentRefreshToken?: string): boolean {
		const isLocal = localStorage.getItem(UserService.cookieName) !== null;
		const storage = isLocal ? localStorage : sessionStorage;
		const stored = this.safeParse<UserLogin>(storage.getItem(UserService.cookieName), null);

		if (!stored || !userLogin) {
			return false;
		}
		if (stored.user?.id !== userLogin.user?.id) {
			return false;
		}
		if (sentRefreshToken !== undefined && stored.refreshToken !== sentRefreshToken) {
			return false;
		}

		const merged: UserLogin = {
			...stored,
			...userLogin,
			// A null refresh token means "keep the one you have" rather than "you have none".
			refreshToken: userLogin.refreshToken ?? stored.refreshToken,
			// Only the interactive sign-in paths set this; the refresh response always carries the
			// default. Taking it would turn an SSO session into a password one at the first silent
			// refresh, and the navbar gates the Entra end-session call on it, so signing out would
			// quietly stop ending the identity-provider session. Harmless while refresh ran once a
			// day, routine now that it runs hourly.
			isSSOLogin: stored.isSSOLogin ?? userLogin.isSSOLogin,
			user: {
				...stored.user,
				...userLogin.user,
				// The refresh response is built for the user's HOME client, so its claims are
				// keyed by that client. Keeping the switched clientId while taking those claims
				// would leave permissions keyed under a client the session is no longer on, and
				// every claim lookup would miss: the UI would silently lose edit and visibility
				// rights until the next re-switch. Client context is not the refresh's business.
				clientId: stored.user?.clientId,
				claims: stored.user?.claims ?? userLogin.user?.claims,
			},
			receivedAt: Date.now(),
		};

		storage.setItem(UserService.cookieName, JSON.stringify(merged));
		this.currentUserSubject.next(merged);
		return true;
	}

	/**
	 * Drops the in-memory session for this tab. Used by the forced-logout path, which owns the
	 * decision about whether shared storage should also be cleared.
	 */
	clearCurrentUser() {
		this.currentUserSubject.next(null);
		this.availableFormsSubject.next([]);
	}

	/** The refresh token as it stands in storage, which another tab may have replaced. */
	readStoredRefreshToken(): string | null {
		const raw = localStorage.getItem(UserService.cookieName) || sessionStorage.getItem(UserService.cookieName);
		return this.safeParse<UserLogin>(raw, null)?.refreshToken ?? null;
	}

	/**
	 * Removes the session from shared storage.
	 *
	 * The keys are enumerated rather than calling clear() so unrelated preferences survive. The
	 * MSAL cache is included: leaving it behind lets the login screen silently re-authenticate,
	 * which would make an expired session look like it never expired.
	 */
	clearSessionStorage() {
		// Stamp who the surviving client selection belonged to before removing the session it
		// came from, so the next sign-in restores it only for the same person. A shared
		// machine's next user should not inherit the previous user's client. When the session
		// is already gone — a sibling tab's forced logout got here first — leave the existing
		// stamp alone: removing it would strip the owner off the surviving pointer, and every
		// sign-in wipes the stamp anyway, so a stale one cannot outlive this logout.
		const dying = this.safeParse<UserLogin>(
			localStorage.getItem(UserService.cookieName) || sessionStorage.getItem(UserService.cookieName),
			null
		);
		if (dying?.user?.id) {
			localStorage.setItem(UserService.clientIdOwnerKey, dying.user.id);
		}

		const keys = [UserService.cookieName, UserService.mappingName, 'availableForms', UserService.msTokenCookie];
		for (const key of keys) {
			localStorage.removeItem(key);
			sessionStorage.removeItem(key);
		}
		this.clearMsalCache();
	}

	private clearMsalCache() {
		const accountKeysKey = `msal.account.keys`;
		const tokenKeysKey = `msal.token.keys.${environment.msEntraClientId}`;

		for (const indexKey of [accountKeysKey, tokenKeysKey]) {
			const index = this.safeParse<Record<string, string[]> | string[]>(localStorage.getItem(indexKey), null);
			if (index) {
				const entries = Array.isArray(index) ? index : Object.values(index).flat();
				for (const entry of entries) {
					if (typeof entry === 'string') {
						localStorage.removeItem(entry);
					}
				}
			}
			localStorage.removeItem(indexKey);
		}
		localStorage.removeItem(UserService.msalAccount);
	}

	// Drops the app-wide lookup caches on client switch. Resolved through the injector so
	// UserService takes no constructor dependencies on feature services.
	private clearLookupCaches(): void {
		this.injector.get(LookupServices).clearReferenceLookupCache();
		this.injector.get(GeneralLedgerLookupServices).clearAccountLookupCache();
		this.injector.get(SubSegmentService).clearSubSegmentLookupCache();
		this.injector.get(ProjectGrantFormService).clearProjectGrantLookupCache();
		this.injector.get(ProjectGrantTaskService).clearTaskLookupCache();
	}

	setClientId(clientId: string, callback: () => void = null, getNewToken = false) {
		let currentStorageIsLocal = true;
		let userData = localStorage.getItem(UserService.cookieName);
		if (!userData) {
			userData = sessionStorage.getItem(UserService.cookieName);
			if (userData) {
				currentStorageIsLocal = false;
			}
		}

		localStorage.setItem(UserService.clientIdCookie, clientId);

		if (userData) {
			let userLogin = this.safeParse<UserLogin>(userData, null);
			if (!userLogin) {
				return;
			}

			if (getNewToken) {
				const storage = currentStorageIsLocal ? localStorage : sessionStorage;

				this.switchClient(clientId).subscribe({
					next: (switched) => {
						// Switching clients continues the existing session: the server issues a
						// fresh access token but no refresh token, because the caller keeps the
						// one it holds. Merging preserves it — replacing the stored object
						// outright would blank it and arm a forced logout at the next refresh.
						const merged: UserLogin = {
							...userLogin,
							...switched,
							refreshToken: switched.refreshToken ?? userLogin.refreshToken,
							// The switch response always carries the default false here; taking it
							// would stop sign-out from ending the Entra session for SSO users.
							isSSOLogin: userLogin.isSSOLogin ?? switched.isSSOLogin,
							user: { ...userLogin.user, ...switched.user, clientId },
							receivedAt: Date.now(),
						};
						storage.setItem(UserService.cookieName, JSON.stringify(merged));

						localStorage.removeItem('availableForms');
						this.availableFormsSubject.next([]);
						this.clearLookupCaches();

						if (!localStorage.getItem(UserService.mappingName)) {
							this.getFormMappingAll().subscribe((data) => {
								this.setFormMappings(data);
							});
						}
						this.currentUserSubject.next(merged);
						this.setDatadogRUMUser();
						if (callback) {
							callback();
						}
					},
					error: () => {
						// Without this the caller's spinner never clears and the failure reads as
						// a hung page. The stored session is deliberately left untouched.
						this.notificationService.showError('Unable to switch client. Please try again.');
						if (callback) {
							callback();
						}
					},
				});
			} else {
				userLogin.user.clientId = clientId;
				if (currentStorageIsLocal) {
					localStorage.setItem(UserService.cookieName, JSON.stringify(userLogin));
				} else {
					sessionStorage.setItem(UserService.cookieName, JSON.stringify(userLogin));
				}
				localStorage.removeItem('availableForms');
				this.availableFormsSubject.next([]);
				this.clearLookupCaches();
				this.currentUserSubject.next(userLogin);
				this.setDatadogRUMUser();
			}
		}
	}

	private safeParse<T>(value: string | null, fallback: T): T {
		if (!value) {
			return fallback;
		}
		try {
			return JSON.parse(value) as T;
		} catch (e) {
			return fallback;
		}
	}

	logout() {
		const userId = this.currentUserValue?.user?.id;
		const token = this.currentUserValue.refreshToken;
		this.logoutApi(userId, token).subscribe(() => {
			localStorage.removeItem(UserService.cookieName);
			sessionStorage.removeItem(UserService.cookieName);
			this.clearAllStorage();
			this.availableFormsSubject.next([]);
			this.currentUserSubject.next(null);
			datadogRum.clearUser();
		});
	}

	setFormMappings(data: any) {
		localStorage.setItem(UserService.mappingName, JSON.stringify(data));
	}

	getUsers(): Observable<User[]> {
		return this.httpClient.get<User[]>(`${environment.apiUrl}users`).pipe();
	}
	getAllUsers(): Observable<User[]> {
		return this.httpClient.get<User[]>(`${environment.apiUrl}users/all`).pipe();
	}

	getUsersWithEntityDepartmentHeadAssignments(clientId: string): Observable<User[]> {
		return this.httpClient.get<User[]>(`${environment.apiUrl}users/clients/${clientId}/entity-department-heads`).pipe();
	}

	updateUserEntityDepartmentAssignments(
		userId: string,
		clientId: string,
		payload: { entityIds: number[]; departmentIds: number[]; signatureUrl?: string; removeSignature?: boolean },
		signatureFile?: File
	): Observable<any> {
		const formData = new FormData();
		formData.append('model', JSON.stringify(payload));
		if (signatureFile) {
			formData.append('Attachment', signatureFile);
		}
		return this.httpClient
			.put(`${environment.apiUrl}users/${userId}/clients/${clientId}/entity-department-assignments`, formData)
			.pipe();
	}

	getIpAddress(): Observable<any> {
		return this.httpClient.get<any>('https://ipv4.myexternalip.com/json').pipe();
	}

	getAclarianUsers(): Observable<User[]> {
		return this.httpClient.get<User[]>(`${environment.apiUrl}users/aclarian`).pipe();
	}

	getUsersForClient(clientId: string): Observable<User[]> {
		return this.httpClient.get<User[]>(`${environment.apiUrl}users/clients/${clientId}`).pipe();
	}

	getUsersDetailedForClient(clientId: string): Observable<User[]> {
		return this.httpClient.get<User[]>(`${environment.apiUrl}users/clients/${clientId}/detailed`).pipe();
	}

	getUsersByClaim(clientId: string, claimValue: string): Observable<User[]> {
		return this.httpClient.get<User[]>(`${environment.apiUrl}users/clients/${clientId}/claim/${claimValue}`).pipe();
	}
	getUser(id: string): Observable<User> {
		return this.httpClient.get<User>(`${environment.apiUrl}users/${id}`).pipe();
	}

	resetMfa(id: string): Observable<any> {
		return this.httpClient.delete(`${environment.apiUrl}users/${id}/resetmfa`).pipe();
	}

	updateUser(item: User): Observable<UserModifyResponse> {
		return this.httpClient.put<UserModifyResponse>(`${environment.apiUrl}users/${item.id}`, item).pipe();
	}

	createUser(item: User): Observable<UserModifyResponse> {
		return this.httpClient.post<UserModifyResponse>(`${environment.apiUrl}users`, item).pipe();
	}

	addRemoveUserToRoles(userId: string, roles: Role[]): Observable<any> {
		return this.httpClient.put<User>(`${environment.apiUrl}users/${userId}/roles`, roles).pipe();
	}

	addRemoveUserToPortalRoles(userId: string, clientId: string, role: RoleItemDto): Observable<any> {
		return this.httpClient.put<User>(`${environment.apiUrl}users/${userId}/${clientId}/portalroles`, role).pipe();
	}

	addRemoveUserToRoleItems(userId: string, clientId: string, roles: RoleItemDto): Observable<any> {
		return this.httpClient.put<User>(`${environment.apiUrl}users/${userId}/${clientId}/roles/items`, roles).pipe();
	}

	getClaimItems(userId: string, clientId: string): Observable<any> {
		return this.httpClient.get<any>(`${environment.apiUrl}users/${userId}/clientId/${clientId}/claims`).pipe();
	}

	getStandaloneAccess(clientId: string): Observable<any> {
		return this.httpClient.get<any>(`${environment.apiUrl}users/clients/${clientId}/standalone-access`).pipe();
	}

	setStandaloneProduct(clientId: string, productId: string, enabled: boolean): Observable<any> {
		return this.httpClient
			.put(`${environment.apiUrl}users/clients/${clientId}/standalone-products/${productId}`, { enabled })
			.pipe();
	}

	getUserRoles(userId: string): Observable<Role[]> {
		return this.httpClient.get<Role[]>(`${environment.apiUrl}users/${userId}/roles`).pipe();
	}

	getFormMappingAll(): Observable<any> {
		return this.httpClient.get<any>(`${environment.apiUrl}users/mapping/forms/all`).pipe();
	}

	addRemoveUserDepartments(userId: string, clientId: string, departments: any[]): Observable<any> {
		return this.httpClient.put<any>(`${environment.apiUrl}users/${userId}/${clientId}/departments`, departments).pipe();
	}

	switchClient(clientId: string): Observable<UserLogin> {
		// The user is resolved from the session server-side; nothing about the caller's identity
		// is sent in the body, so there is no user id to pass.
		return this.httpClient.post<UserLogin>(`${environment.apiUrl}authentication/switch-client/${clientId}`, {}).pipe();
	}

	isAdmin(): boolean {
		if (this.hasRole('SYS_ADMIN')) {
			return true;
		}

		let currentClientId = this.currentUserValue?.user?.clientId;
		if (currentClientId == environment.aclarianId) {
			return false;
		}

		return this.hasRole('Aclarian Support');
	}
	isAdminOnly(): boolean {
		return this.hasRole('SYS_ADMIN');
	}
	isUserAccessAdmin(): boolean {
		return this.hasRole('USER_ACCESS_ADMIN');
	}
	canManageAdministrativeRoles(): boolean {
		return this.hasRole('SYS_ADMIN') || this.hasRole('USER_ACCESS_ADMIN');
	}
	canViewAdministrativeRoleAudit(): boolean {
		return this.isSysAdminOrAclarianSupport();
	}
	// Unlike isAdmin(), this does not exclude Aclarian Support while on the Aclarian client.
	isSysAdminOrAclarianSupport(): boolean {
		return this.hasRole('SYS_ADMIN') || this.hasRole('Aclarian Support');
	}
	isReadOnlyView(): boolean {
		if (this.isAdmin() || this.hasRole('Aclarian Support')) {
			return false;
		}
		return this.hasRole('CLIENT_SUPPORT_READ_ONLY');
	}
	isAuthorizedUser(): boolean {
		if (this.currentUserValue != null) {
			return this.currentUserValue.user.claims;
		}
		return false;
	}

	///deprecated
	isClientAdmin(clientId: string): boolean {
		return this.isAdmin();
	}

	hasRole(roleName: string) {
		if (this.currentUserValue != null) {
			const role = this.currentUserValue.user.roles.find((t) => t.name === roleName);
			return role != null || role != undefined;
		}
		return false;
	}

	isAclarianEmployee() {
		let result = false;
		if (this.currentUserValue && this.currentUserValue.user) {
			result = this.currentUserValue.user.isAclarianEmployee;
		}

		return result;
	}

	canEditFormById(formId: string) {
		if (this.isReadOnlyView()) {
			return false;
		}
		return this.getClaim(formId)?.edit == true || this.isAdmin();
	}

	canVisibleFormById(formId: string) {
		return this.getClaim(formId)?.visible == true || this.isAdmin();
	}

	canAccessResource(resourceId: string): Observable<boolean> {
		if (this.isAdmin()) {
			return of(true);
		}

		const user = this.currentUserValue?.user;
		const clientId = user?.clientId;

		if (!user || !clientId) {
			return of(false);
		}

		return this.availableForms$.pipe(
			filter((forms) => forms.length > 0),
			take(1),
			map((forms) => {
				const isEnabledForClient = forms.some((form) => form.id === resourceId && form.isEnabled);

				if (!isEnabledForClient) {
					return false;
				}

				const isReadOnly = this.isReadOnlyView();
				const hasUserPermission = this.canVisibleFormById(resourceId);

				return hasUserPermission || isReadOnly;
			})
		);
	}

	isAdminToEditForm(clientId: string, formId: string = '') {
		if (this.isReadOnlyView()) {
			return false;
		}
		return this.isAdmin() || this.getClaim(formId)?.edit == true;
	}

	getClaim(claimId): ClaimItem {
		let user = this.currentUserValue.user;
		return user?.claims[user.clientId]?.claims.find((x) => x.id == claimId);
	}

	canEditForm(form: WorkflowForm, formId?: string) {
		if (this.isReadOnlyView()) {
			return false;
		}
		let currentStep = form?.workflowStep ?? form?.workflowSteps?.find((t) => t.isCurrent)?.name;
		if (currentStep) {
			currentStep = currentStep.toLowerCase();
		}
		if (formId == null) {
			formId = this.getFormKeyByName(form.workflowName);
		}

		return (
			form.declinedReason == null &&
			((form.createdBy == this.currentUserValue.user.id &&
				(currentStep == 'workflow started' || currentStep == 'draft')) ||
				(this.isAdminToEditForm(form.clientId, formId) && (this.isAdmin() || currentStep !== 'completed')))
		);
	}

	canEditFormBypassWorkflowStatus(form: WorkflowForm) {
		if (this.isReadOnlyView()) {
			return false;
		}
		return this.isAdminToEditForm(form.clientId, this.getFormKeyByName(form.workflowName));
	}

	getClaimDepartments(clientId: string, formId: string) {
		const claim = this.getClaim(`${formId}-department`);
		if (claim) {
			return claim.departmentIds;
		}
		return [];
	}

	getClaimDepartmentsByList(clientId: string, formIds: string[]) {
		const result: { formId: string; departmentIds: number[] }[] = [];
		for (const formId of formIds) {
			const claim = this.getClaim(`${formId}-department`);
			result.push({
				formId,
				departmentIds: claim ? claim.departmentIds : [],
			});
		}
		return result;
	}

	canEditFormByName(clientId: string, formName: string) {
		if (this.isReadOnlyView()) {
			return false;
		}
		return this.canEditForm({
			comments: [],
			workflowName: formName,
			allApprovers: [],
			clientId: clientId,
			completedDate: undefined,
			createdBy: '',
			createdDate: undefined,
			currentApprovers: [],
			declinedReason: null,
			id: '',
			isActive: true,
			isCompleted: false,
			requestedBy: '',
			workflowId: 0,
			workflowStep: '',
			workflowSteps: [],
		});
	}

	canApproveForm(form: WorkflowForm, clientId: string) {
		if (this.isReadOnlyView()) {
			return false;
		}
		return this.isAdmin() || this.utilityService.isApprover(form, this.currentUserValue.user.id);
	}

	canSubmitForApproval(form: WorkflowForm, formId: string) {
		if (this.isReadOnlyView()) {
			return false;
		}
		return (
			(form.workflowStep?.toLowerCase() == 'draft' || form.workflowStep?.toLowerCase() == 'workflow started') &&
			(form.createdBy == this.currentUserValue.user.id || this.isAdminToEditForm(form.clientId, formId))
		);
	}

	canSendForReview(form: WorkflowForm, userId: string) {
		if (this.isReadOnlyView()) {
			return false;
		}
		let currentStep = form?.workflowSteps?.find((t) => t.isCurrent)?.name ?? form?.workflowStep;
		if (currentStep) {
			currentStep = currentStep.toLowerCase();
		}

		return (
			currentStep != 'workflow started' &&
			currentStep != 'completed' &&
			(this.utilityService.canSendForReview(form, userId) || this.isAdmin())
		);
	}

	loginAs(id: string) {
		return this.httpClient.post<UserLogin>(`${environment.apiUrl}authentication/secret-login-request`, { id }).pipe(
			map((userLogin) => {
				this.clearAllStorage();
				this.setClientIdIfEmpty(userLogin.user);
				sessionStorage.setItem(UserService.cookieName, JSON.stringify(userLogin));

				if (!localStorage.getItem(UserService.mappingName)) {
					this.getFormMappingAll().subscribe((data) => {
						this.setFormMappings(data);
					});
				}
				this.currentUserSubject.next(userLogin);
				return userLogin;
			})
		);
	}

	validate2FA(email: any, verificationCode: string): Observable<UserLogin> {
		return this.httpClient
			.post<UserLogin>(`${environment.apiUrl}authentication/2fa`, {
				email,
				verificationCode,
			})
			.pipe();
	}

	logoutApi(userId: string, refreshToken: string): Observable<UserLogin> {
		return this.httpClient
			.post<UserLogin>(`${environment.apiUrl}authentication/logout`, { userId, refreshToken })
			.pipe();
	}

	private setClientIdIfEmpty(user: User) {
		if (user.clients.length == 1) {
			user.clientId = user.clients[0].clientId;
			this.setClientId(user.clientId);
		}
	}
	getUserAccessReport(clientId: string, filter: any): Observable<any[]> {
		return this.httpClient
			.post<any[]>(`${environment.apiUrl}standard-report/clients/${clientId}/get-access-report`, filter)
			.pipe();
	}

	getUserAccessReportV2(clientId: string, filteredForms: AvailableForm[]): Observable<any[]> {
		return this.httpClient.post<any[]>(
			`${environment.apiUrl}standard-report/clients/${clientId}/get-access-report-v2`,
			filteredForms
		);
	}
	enqueueFullAccessExport(
		clientId: string,
		request: {
			includeBudgetRoles: boolean;
			includeEntityDepartmentAssignments: boolean;
			includeCustomRoles: boolean;
		}
	): Observable<{ jobId: string }> {
		return this.httpClient.post<{ jobId: string }>(
			`${environment.apiUrl}standard-report/clients/${clientId}/enqueue-full-access-export`,
			request
		);
	}
	getFullAccessExportJobs(clientId: string): Observable<
		Array<{
			jobId: string;
			status: 'Enqueued' | 'Processing' | 'Succeeded' | 'Failed';
			enqueuedAt: string | null;
			succeededAt: string | null;
			fileUrl: string | null;
		}>
	> {
		return this.httpClient.get<
			Array<{
				jobId: string;
				status: 'Enqueued' | 'Processing' | 'Succeeded' | 'Failed';
				enqueuedAt: string | null;
				succeededAt: string | null;
				fileUrl: string | null;
			}>
		>(`${environment.apiUrl}standard-report/clients/${clientId}/export-jobs`);
	}
	getAuditLogReportLegacy(clientId: string, filter: any): Observable<any[]> {
		return this.httpClient
			.post<any[]>(`${environment.apiUrl}standard-report/clients/${clientId}/get-audit-log-report`, filter)
			.pipe();
	}

	setMsToken(msToken: string) {
		localStorage.setItem(UserService.msTokenCookie, msToken);
	}

	getMsalAccessToken() {
		const msTokenKeys = localStorage.getItem(`msal.token.keys.${environment.msEntraClientId}`);
		if (msTokenKeys == null) {
			console.log('msalToken not available');
			return null;
		}
		const accessTokenKey = JSON.parse(msTokenKeys).accessToken;
		const accessTokenObject = localStorage.getItem(accessTokenKey);
		return JSON.parse(accessTokenObject).secret;
	}

	getMsalUserName() {
		var msalAccountKeys = localStorage.getItem('msal.account.keys');
		if (msalAccountKeys == null) {
			console.log('msal account keys not available');
			return null;
		}
		const accountKey = JSON.parse(msalAccountKeys)[0];
		const msalAccount = JSON.parse(localStorage.getItem(accountKey));
		localStorage.setItem(UserService.msalAccount, JSON.stringify(msalAccount));
		return msalAccount.username;
	}

	clearMsLogin() {
		this.clearAllStorage();
		datadogRum.clearUser();
	}

	isSSOLogin() {
		return this.currentUserValue.isSSOLogin;
	}

	getMsalAccount() {
		return JSON.parse(localStorage.getItem(UserService.msalAccount));
	}

	importUsers(users: any[]): Observable<any> {
		return this.httpClient.post<any>(`${environment.apiUrl}clients/import-users`, users).pipe();
	}
	assignUsersToAdminRole(roleId: string, userIds: string[]): Observable<any> {
		return this.httpClient.put<any[]>(`${environment.apiUrl}users/${roleId}/assign-admin-role`, userIds).pipe();
	}
	getAdministrativeRoleUsers(): Observable<any[]> {
		return this.httpClient.get<any[]>(`${environment.apiUrl}users/administrative-role-users`);
	}
	getUsersWithBudgetRoleAssignments(clientId: string): Observable<any[]> {
		return this.httpClient.get<any[]>(`${environment.apiUrl}users/clients/${clientId}/budget-role-assignments`);
	}
	bulkUpdateBudgetRoleAssignments(clientId: string, payload: any): Observable<any> {
		return this.httpClient.put(`${environment.apiUrl}users/clients/${clientId}/budget-role-assignments/bulk`, payload);
	}

	updateUserProfileSettings(settings: ProfileSettings): Observable<{ message: string }> {
		return this.httpClient.put<{ message: string }>(
			`${environment.apiUrl}users/update-user-profile-settings`,
			settings
		);
	}
}
